// api.js — einheitlicher API-Client. Wirft NIE:
//   { ok: true, status, data }  |  { ok: false, status, code, message, data }
// status 0 = Netzwerkfehler/Abbruch/Timeout (code 'network' | 'aborted' | 'timeout').
// Bei 401 wird onUnauthorized aufgerufen (Shell leitet zur Anmeldung).
// Nach jeder erfolgreichen Änderung (nicht GET) onMutated({ method, url }) — z. B. für die Erfolgs-Feier der App.

const hooks = { onUnauthorized: null, onError: null, onMutated: null };
export function configureApi(opts = {}) { Object.assign(hooks, opts); }

let pageController = null;
/** Neuer Abbruch-Bereich für einen Seitenwechsel: laufende Seiten-Anfragen werden abgebrochen. */
export function newPageScope() {
  if (pageController) pageController.abort();
  pageController = typeof AbortController !== 'undefined' ? new AbortController() : null;
  return pageController && pageController.signal;
}

function combine(signals) {
  const list = signals.filter(Boolean);
  if (list.length <= 1) return list[0];
  if (typeof AbortSignal !== 'undefined' && AbortSignal.any) return AbortSignal.any(list);
  const c = new AbortController();
  for (const s of list) { if (s.aborted) { c.abort(); break; } s.addEventListener('abort', () => c.abort(), { once: true }); }
  return c.signal;
}

// Ab pagehide bekommt jede Anfrage keepalive (z. B. die Rückgängig-Snackbar, die erst beim Entladen sendet).
// Nötig wegen des Service Workers: steuert er die Seite, verwirft Chromium beim Entladen jede Anfrage,
// die er nur durchreicht — keepalive-Anfragen schickt public/sw.js selbst (gemessen 2026-10-18;
// ohne Service Worker kam auch ein gewöhnlicher fetch an).
let unloading = false;
if (typeof addEventListener === 'function') {
  addEventListener('pagehide', () => { unloading = true; });
  addEventListener('pageshow', () => { unloading = false; });
}
export const isUnloading = () => unloading;

export async function request(method, url, { body, timeout = 20000, signal, page = false, headers = {}, fetchImpl, keepalive = false } = {}) {
  const f = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!f) return { ok: false, status: 0, code: 'network', message: 'fetch unavailable', data: null };
  const timer = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timedOut = false;
  const t = timer ? setTimeout(() => { timedOut = true; timer.abort(); }, timeout) : null;
  const sig = combine([timer && timer.signal, signal, page && pageController ? pageController.signal : null]);
  const init = { method, credentials: 'same-origin', headers: { Accept: 'application/json', ...headers }, signal: sig };
  if (keepalive || unloading) init.keepalive = true; // überlebt das Verlassen der Seite
  if (body !== undefined) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) init.body = body;
    else { init.body = JSON.stringify(body); init.headers['Content-Type'] = 'application/json'; }
  }
  let res;
  try {
    res = await f(url, init);
  } catch (e) {
    if (t) clearTimeout(t);
    const code = timedOut ? 'timeout' : (e && e.name === 'AbortError' ? 'aborted' : 'network');
    const out = { ok: false, status: 0, code, message: (e && e.message) || code, data: null };
    if (code !== 'aborted' && hooks.onError) hooks.onError(out, { method, url });
    return out;
  }
  if (t) clearTimeout(t);
  let data = null;
  const ct = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
  try { data = ct.includes('application/json') ? await res.json() : await res.text(); } catch { data = null; }
  if (res.ok) {
    if (method !== 'GET' && hooks.onMutated) { try { hooks.onMutated({ method, url }); } catch { /* nie die Anfrage stören */ } }
    return { ok: true, status: res.status, data };
  }
  const out = {
    ok: false, status: res.status,
    code: (data && typeof data === 'object' && (data.code || data.error)) || 'http_' + res.status,
    message: (data && typeof data === 'object' && (data.message || data.error)) || (typeof data === 'string' && data) || res.statusText || 'Fehler',
    data,
  };
  if (res.status === 401 && hooks.onUnauthorized) hooks.onUnauthorized(out);
  else if (hooks.onError) hooks.onError(out, { method, url });
  return out;
}

export const api = {
  get: (url, o) => request('GET', url, o),
  post: (url, body, o) => request('POST', url, { ...o, body }),
  put: (url, body, o) => request('PUT', url, { ...o, body }),
  patch: (url, body, o) => request('PATCH', url, { ...o, body }),
  del: (url, o) => request('DELETE', url, o),
};
